import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  UserPlus,
  Trash2,
  Crown,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { useIsAppAdmin } from "../hooks/useIsAppAdmin";
import { ADMIN_EMAILS } from "../lib/adminEmails";

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AppAdminRow = {
  user_id: string;
  email: string | null;
  created_at: string;
};

export default function AdminAppAdmins() {
  const navigate = useNavigate();
  const { isAdmin: canAccess, loading: checkingAccess } = useIsAppAdmin();

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [admins, setAdmins] = useState<AppAdminRow[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    const { data } = await supabase
      .from("app_admins")
      .select("user_id, email, created_at")
      .order("created_at", { ascending: true });
    setAdmins((data ?? []) as AppAdminRow[]);
    setLoadingAdmins(false);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id ?? null);
    });
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    loadAdmins();
  }, [canAccess, loadAdmins]);

  async function handleGrant(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const raw = input.trim();
    if (!raw) {
      setErrorMsg("Indique l'email ou l'UUID de l'utilisateur.");
      return;
    }

    setSaving(true);

    try {
      const params = UUID_RE.test(raw)
        ? { _email: null, _user_id: raw }
        : EMAIL_RE.test(raw)
        ? { _email: raw, _user_id: null }
        : null;

      if (!params) {
        throw new Error("Entre une adresse email valide ou un UUID.");
      }

      const { data, error } = await supabase.rpc("admin_grant_app_admin", params);
      if (error) throw error;

      const granted = Array.isArray(data) ? data[0] : data;
      setSuccessMsg(`${granted?.email ?? raw} est maintenant admin de l'application.`);
      setInput("");
      await loadAdmins();
    } catch (err: any) {
      const message = String(err?.message || "");
      if (message.includes("user not found")) {
        setErrorMsg("Aucun utilisateur trouvé avec cet email ou cet UUID.");
      } else if (message.includes("not authorized")) {
        setErrorMsg("Tu n'as plus les droits pour effectuer cette action.");
      } else {
        setErrorMsg(message || "Impossible de donner le rôle admin.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(userId: string) {
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const { error } = await supabase.rpc("admin_revoke_app_admin", {
        _user_id: userId,
      });
      if (error) throw error;
      await loadAdmins();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible de retirer ce rôle admin.");
    }
  }

  if (checkingAccess) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Vérification des accès…
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Accès refusé</div>
              <div className="text-sm mt-1">
                Cette page est réservée aux administrateurs de l’application.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Espace admin</div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Administrateurs de l'application
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Donne ou retire le rôle admin de l'app à un utilisateur existant.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      </div>

      <form
        onSubmit={handleGrant}
        className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6 space-y-5"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-gray-100 dark:bg-gray-800 p-3">
            <UserPlus className="h-6 w-6 text-gray-700 dark:text-gray-200" />
          </div>
          <div>
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Donner le rôle admin
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Identifie l'utilisateur par son <span className="font-medium">email</span> ou son{" "}
              <span className="font-medium">UUID</span>.
            </div>
          </div>
        </div>

        <div>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ex. jdupont@email.com ou 3f2a1b4c-..."
            className="w-full px-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            disabled={saving}
            required
          />
        </div>

        {errorMsg && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-200">
            {successMsg}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !input.trim()}
            className={cx(
              "inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-medium",
              "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
              (saving || !input.trim()) && "opacity-60 cursor-not-allowed"
            )}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {saving ? "Attribution..." : "Donner le rôle admin"}
          </button>
        </div>
      </form>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6">
        <div className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Admins actuels
        </div>

        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 px-4 py-3 mb-3 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Crown className="h-4 w-4 text-yellow-500 shrink-0" />
          <span>
            {ADMIN_EMAILS.join(", ")} — admin racine (défini dans le code, toujours actif).
          </span>
        </div>

        {loadingAdmins ? (
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        ) : admins.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Aucun admin ajouté via cette page pour le moment.
          </div>
        ) : (
          <div className="space-y-2">
            {admins.map((a) => (
              <div
                key={a.user_id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 dark:border-gray-800 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {a.email || a.user_id}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Ajouté le{" "}
                    {new Date(a.created_at).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRevoke(a.user_id)}
                  disabled={a.user_id === currentUserId}
                  title={
                    a.user_id === currentUserId
                      ? "Tu ne peux pas retirer ton propre accès ici"
                      : "Retirer le rôle admin"
                  }
                  className={cx(
                    "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition",
                    a.user_id === currentUserId
                      ? "border-gray-200 dark:border-gray-800 text-gray-300 dark:text-gray-700 cursor-not-allowed"
                      : "border-red-200 dark:border-red-900/40 bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/20"
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
